import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'modules/hooks/appHooks';
import { BodyText, PageWithHeader, Loader, Button, ButtonType } from '../../ui';
import {
  ModalError,
  InvisibleInput,
  ResponsiveModal,
  StyledDragAndDrop,
  StyledLabel,
  StyledUploadDiv,
  TextWrapper,
  StyledBodyText,
} from './styled';
import { useToastContainer } from '../../layout/toastContainer';
import { NavigationAndActionUpload } from '../../ui/icons';
import { DocumentationPathsEnum } from '../routes';
import { DocumentationSelectors } from '../redux';
import { AllowedDocumentFormats, Category, ModalState } from './helpers';
import { isDesktopResolution } from '../../utils';
import { uploadFinished } from '../redux/slice';
import { useFileManagement } from '../../hooks/useFileManagement';
import { DocumentCategory } from '../../types/Document';
import { UploadedFilesList } from './common/UploadedFilesList';
import { DocumentDetailsForm } from './common/DocumentDetailsForm';

export const DocumentationList: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { addToast } = useToastContainer();

  const [modalState, setModalState] = useState<ModalState>({ isOpen: false });
  const [uploadHover, setUploadHover] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const formRef = useRef<HTMLFormElement>();

  const { files, setFiles, addFile, removeFile, handleUpload } =
    useFileManagement(setErrorMessage);

  const documentation = useAppSelector(
    DocumentationSelectors.selectDocumentation,
  );
  const uploadError = useAppSelector(DocumentationSelectors.selectUploadError);
  const uploadSuccess = useAppSelector(
    DocumentationSelectors.selectUploadSuccess,
  );
  const uploadSubmitActive = useAppSelector(
    DocumentationSelectors.selectUploadLoading,
  );

  const categories: Category[] = [
    {
      documentCategory: DocumentCategory.KASKO_INSURANCE_POLICY,
      displayName: t('documentationPage.kaskoPolicy'),
    },
    {
      documentCategory: DocumentCategory.AO_POLICY,
      displayName: t('documentationPage.aoPolicy'),
    },
    {
      documentCategory: DocumentCategory.PROPERTY_POLICY,
      displayName: t('documentationPage.propertyPolicy'),
    },
    {
      documentCategory: DocumentCategory.COMPENSATION_CLAIM,
      displayName: t('documentationPage.compensationClaim'),
    },
    {
      documentCategory: DocumentCategory.CONTRACT_TERMINATION,
      displayName: t('documentationPage.contractTermination'),
    },
    {
      documentCategory: DocumentCategory.EARLY_CONTRACT_TERMINATION,
      displayName: t('documentationPage.earlyContractTermination'),
    },
  ];

  const handlePlusClick = (category: Category) => {
    if (isDesktopResolution()) {
      setModalState({
        isOpen: true,
        documentCategory: category.documentCategory,
      });
    } else {
      history.push({
        pathname: DocumentationPathsEnum.mobileUploadPage,
        state: { category },
      });
    }
  };

  const closeModal = useCallback(() => {
    setModalState({ isOpen: false });
    setFiles([]);
    setErrorMessage('');
  }, [setFiles]);

  const onDragOver = (e: any) => {
    const event = e as Event;
    event.stopPropagation();
    event.preventDefault();
  };

  useEffect(() => {
    if (uploadSuccess) {
      closeModal();
      addToast({
        title: t('documentationPage.uploadSuccessTitle'),
        text: t('documentationPage.uploadSuccessMessage'),
      });
      dispatch(uploadFinished());
    } else if (uploadError) {
      setErrorMessage(uploadError);
    }
  }, [uploadSuccess, uploadError, addToast, t, dispatch, closeModal]);

  const handleUploadButtonClick = () => {
    formRef.current?.dispatchEvent(
      new Event('submit', { cancelable: true, bubbles: true }),
    );

    if (!files.length) {
      setErrorMessage(t('documentationPage.emptyFileListMessage'));
    }
  };

  return (
    <PageWithHeader
      breadcrumbs={[
        { text: t('documentationPage.breadcrumbs.home'), url: '/' },
        { text: t('documentationPage.breadcrumbs.documentation'), url: '' },
      ]}
      title={t('documentationPage.documentation')}>
      <StyledBodyText style={{marginBottom: "unset", display: "inline-block", paddingRight: "7px"}}>
        {t('documentationPage.minDocumentationListText')}
      </StyledBodyText>
      <a href="https://unicreditleasing.hr/home/o-leasingu/dokumentacija">https://unicreditleasing.hr/home/o-leasingu/dokumentacija</a>
      <StyledBodyText style={{marginBottom: "unset", display: "inline-block", paddingRight: "7px"}}>
        {t('documentationPage.minDocumentationListTextNext')}
      </StyledBodyText>
      <a href="https://unicreditleasing.hr/home/podrska">Poslovnice UNICREDIT LEASING CROATIA d.o.o. - UniCredit Leasing Croatia</a>
      <br/> <br/>
      <Button
        type={ButtonType.Primary}
        text={t('documentationPage.uploadButtonText')}
        // hardcode KASKO as url parameter for now - API call uploadDocuments will ignore this parameter
        onClick={() => handlePlusClick(categories[0])}
      />
      {/* FRON-541 commented for release day */}
      {/* <DocumentationWrapper>
        {categories.map((category) => (
          <Accordion
            key={category.documentCategory}
            addButton
            onAdd={() => handlePlusClick(category)}
            title={category.displayName}
            onOpen={() => {
              dispatch(
                DocumentationThunks.fetchDocuments({
                  documentCategory: category.documentCategory,
                }),
              );
            }}>
            {documentation.categories[
              category.documentCategory
            ]?.documents?.map((document: DocumentFile) =>
              getDocumentListItem(document),
            )}
          </Accordion>
        ))}
      </DocumentationWrapper> */}
      <ResponsiveModal
        title={t('documentationPage.addDocument')}
        visible={modalState.isOpen}
        primaryButtonText={t('documentationPage.add')}
        secondaryButtonText={t('documentationPage.cancel')}
        primaryAction={handleUploadButtonClick}
        secondaryAction={closeModal}
        onClose={closeModal}>
        <DocumentDetailsForm
          documentCategory={modalState.documentCategory}
          uploadedFilesExists={files.length > 0}
          uploadDocument={handleUpload}
          formRef={formRef}
        />

        <Loader active={uploadSubmitActive}>
          <StyledDragAndDrop
            htmlFor="file_drop"
            onDragOver={onDragOver}
            onDrop={(e) => {
              addFile(e);
              setUploadHover(false);
            }}
            onDragEnterCapture={() => setUploadHover(true)}
            onDragLeaveCapture={() => setUploadHover(false)}
            dragOver={uploadHover}>
            <InvisibleInput
              type="file"
              id="file_drop"
              accept={AllowedDocumentFormats}
              multiple
              onClick={(e) => {
                e.preventDefault();
              }}
            />
            <StyledLabel>
              {t('documentationPage.uploadInstruction')}
            </StyledLabel>
            <TextWrapper>
              <BodyText>{t('documentationPage.uploadAdditionalText')}</BodyText>
              <BodyText>
                {t('documentationPage.uploadAdditionalText2')}
              </BodyText>
            </TextWrapper>
            <label htmlFor="file_click">
              <StyledUploadDiv>
                <NavigationAndActionUpload />
                <p>{t('documentationPage.uploadButtonText')}</p>
              </StyledUploadDiv>
              <InvisibleInput
                type="file"
                id="file_click"
                accept={AllowedDocumentFormats}
                multiple
                onInput={(e) => addFile(e)}
              />
            </label>
          </StyledDragAndDrop>
        </Loader>
        <ModalError hasError={!!errorMessage}>
          <p>{errorMessage}</p>
        </ModalError>
        <UploadedFilesList files={files} removeFile={removeFile} />
      </ResponsiveModal>
    </PageWithHeader>
  );
};
